import {
    DEBOUNCE_TIMEOUT,
    FORM_ADDRESS_NAME,
    FORM_CITY_NAME,
    FORM_ZIP,
    LOGIN_INPUT_PREFIX,
    PROPERTY_APARTMENT_GROUP,
    PROPERTY_HOUSE_GROUP,
    ZIP_REGEX,
} from 'constants/Global';

import { getValueFromPath } from './ObjectUtils';
import { isNonEmpty } from './StringUtils';
import { translate } from './Translate';

/**
 * Regex for checking if zip is in IdF.
 * @type {RegExp}
 */
const ILE_DE_FRANCE_ZIPS = /^(?:75|77|78|91|92|93|94|95)/;

/**
 * Scroll view to first error encountered.
 */
export function scrollToFirstError() {
    setTimeout(() => {
        const errorFields = document.getElementsByClassName('field-error');
        if (errorFields.length > 0) {
            const top = errorFields[0].offsetTop - 100;

            if ('scrollBehavior' in document.documentElement.style) {
                window.scrollTo({
                    top,
                    left: 0,
                    behavior: 'smooth',
                });
            } else {
                window.scrollTo(0, top);
            }
        }
    }, DEBOUNCE_TIMEOUT);
}

/**
 * Determine if given address is in province (outside Ile de France) or not.
 * @param {Object} values Form values
 * @return {Boolean} True if address is in province, false otherwise.
 */
export function isProvince({ zip }) {
    return String(zip).length === 5 && !ILE_DE_FRANCE_ZIPS.test(zip);
}

/**
 * Determine if given address is in province (outside Ile de France) or not.
 * @param {String} zip Postal code
 * @return {Boolean} True if postal code is valid
 */
export function isValidZip(zip) {
    if (typeof zip !== 'string') {
        return false;
    }
    return ZIP_REGEX.test(zip);
}

/**
 * Determine if address is valid.
 * @param {{addressName: String, cityName: Number, zip: String}} address
 * @return {Boolean} True if all address properties are valid.
 */
export function isValidAddress({ addressName, cityName, zip }) {
    return isNonEmpty(addressName) && isNonEmpty(cityName) && isValidZip(zip);
}

/**
 * Determine if a city / postal code is valid.
 * @param {{cityName: Number, zip: String}} address
 * @return {Boolean} True if properties are valid.
 */
export function isValidCityZip({ cityName, zip }) {
    return isNonEmpty(cityName) && isValidZip(zip);
}

/**
 * Function to return true if only the address field is empty
 * @param {Object} values   Form values
 * @return {Boolean}
 */
export function onlyAddressFieldIsEmpty(values) {
    return (
        isValidZip(values[FORM_ZIP]) &&
        values[FORM_CITY_NAME] !== '' &&
        values[FORM_ADDRESS_NAME] === ''
    );
}

/**
 * Determine if user address
 * @param {Array} suggestedAddresses
 * @return {Boolean} True if we must provide suggested addresses to user
 */
export function hasSuggestedAddresses({ suggestedAddresses }) {
    return suggestedAddresses && suggestedAddresses.length > 0;
}

/**
 * Compute address step subtitle (for complete or verify)
 * @param {Object} values Form values
 * @return {String} Subtitle of adress step
 */
export function addressStepSubtitle(values) {
    if (values._zip_from_url && values._address_name_from_url && values._city_name_from_url) {
        return translate('Vérifiez votre adresse');
    }
    return translate('Commencez par estimer votre bien');
}

/**
 * Determine if current values refer to an apartment.
 * @param {Object} values Form values
 * @return {Boolean} True if form is an apartment, false otherwise.
 */
export function isApartment(values) {
    return PROPERTY_APARTMENT_GROUP.indexOf(values._property_type) !== -1;
}

/**
 * Determine if current values refer to a house.
 * @param {Object} values Form values
 * @return {Boolean} True if form is aa house, false otherwise.
 */
export function isHouse(values) {
    return PROPERTY_HOUSE_GROUP.indexOf(values._property_type) !== -1;
}

/**
 * Determine if current values refer to a property owner.
 * @param {Object} values Form values
 * @return {Boolean} True if user is owner, false otherwise
 */
export function isOwner(values) {
    return !!values.profile_owner && !values.user_is_pro;
}

/**
 * Determine if current values refer to an estima in zone media.
 * @param {Object} values Form values
 * @return {Boolean} True if user is in zone media, false otherwise
 */
export function isZoneMedia(values) {
    return Boolean(values._zoneMedia);
}

/**
 * Extract login values
 * @param {Object} values Form values
 * @return {Object} Login specific values
 */
export function extractLoginValues(values) {
    return Object.keys(values)
        .filter((fieldName) => fieldName.startsWith(LOGIN_INPUT_PREFIX))
        .reduce((previous, current) => {
            // eslint-disable-next-line no-param-reassign
            previous[current.replace(LOGIN_INPUT_PREFIX, '')] = values[current];
            return previous;
        }, {});
}

/**
 * Determine if current step number refer to a Barometre form.
 * @param {number} stepNumber Step numer
 * @return {Boolean} True if form is Barometre, false otherwise
 */
export function isBarometreStepNumber(stepNumber) {
    return stepNumber === 0;
}

/**
 * Determine if Barometre form should have phone retrieval step
 * @param {Object} props
 * @param {Object} props.auth
 * @param {Object} props.values
 */
export function hasPhoneRetrieval({ auth, values }) {
    return getValueFromPath(auth, 'contact.phone') === null && !isZoneMedia(values);
}
